<script>
import FileDownloadLink from '@components/FileDownloadLink';
import utility from '@mixins/utility';
import { mapState } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import BlockingContainer from '@components/BlockingContainer';
import Layout from '@layouts/main';
import CarModel from '@models/Car';
import flatPickr from 'vue-flatpickr-component';
import debounce from 'lodash/debounce';
import HeaderComponent from '@components/HeaderComponent';

export default {
  page() {
    return {
      title: this.$t('titles.cars'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    BlockingContainer,
    flatPickr,
    FileDownloadLink,
    HeaderComponent,
  },
  mixins: [utility],
  data() {
    return {
      showMoreOptions: false,
      sortBy: 'id',
      sortDesc: true,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'id', sortable: true },
        { key: 'license_plate', label: this.$t('table_labels.lic_plate'), sortable: true },
        { key: 'car_type', label: this.$t('table_labels.car_type'), sortable: true },
        { key: 'car_owner', label: this.$t('table_labels.owner'), sortable: true },
        { key: 'verified', label: this.$t('table_labels.verified'), sortable: true },
        { key: 'sva_print', label: this.$t('table_labels.sva'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      filters: {
        license_plate: null,
      },
      svaExport: {
        fromDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        toDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
      },
      includingZub: false,
      isBusy: false,
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
    ...mapState({
      dateConfig: (state) => state.common.dateConfig,
      svaExportFromDateConfig: (state) =>
        Object.assign({}, state.common.dateConfig, {
          defaultDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        }),
      svaExportToDateConfig: (state) =>
        Object.assign({}, state.common.dateConfig, {
          defaultDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0),
        }),
    }),
    svaExportParams() {
      const params = new URLSearchParams();
      params.append('date_from', this.formatDate(this.svaExport.fromDate));
      params.append('date_to', this.formatDate(this.svaExport.toDate));
      return params;
    },
  },
  watch: {
    filters: {
      handler() {
        this.$refs.asyncTable.refresh();
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    formatDate(date) {
      
      if (!date) {
        return '';
      }

      if (typeof date === 'object') {
        let month = '' + (date.getMonth() + 1);
        let day = '' + date.getDate();
        let year = date.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;
        return [year, month, day].join('-');
      } else if (typeof date === 'string') {
        let splitted = date.split(' ');
        return splitted[0];
      }
    },
    downloadPDF(url) {
      return this.$http
        .get(url, { responseType: 'blob', params: this.svaExportParams })
        .then((response) => {
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          const fileName = this.getFileNameFromContentDisposition(String(headers['content-disposition']));
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = fileName || (
            // the date should be current date in format dMY
            url === 'admin/cars/export/sva'
              ? `${this.formatDate(new Date())} SVA Export`
              : `${this.formatDate(new Date())} SVA Export ZUB`
          );
          link.click();
        });
    },
    exportSVAPdf() {
      this.$wait.start('downloading-pdf');
      this.downloadPDF('admin/cars/export/sva')
        .then(() => (this.includingZub ? this.downloadPDF('admin/cars/export/zub-images') : null))
        .catch(() => {})
        .finally(() => this.$wait.end('downloading-pdf'));
    },
    update(item) {
      this.$nextTick(() => {
        this.$wait.start('car processing');
        new CarModel(Object.assign({}, this.car, item))
          .save()
          .then((response) => {
            this.$refs.asyncTable.refresh();
            this.$wait.end('car processing');
          })
          .catch(() => this.$wait.end('car loading'));
      });
    },
    itemsProvider(ctx) {
      return CarModel.orderBy(this.sortDir + ctx.sortBy)
        .include('car_type', 'car_owner')
        .where('license_plate', this.filters.license_plate)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.car') }))) {
        this.$wait.start('car processing');
        new CarModel({ id })
          .delete()
          .then(() => {
            this.$wait.end('car processing');
            this.$refs.asyncTable.refresh();
          })
          .catch(() => {
            this.$wait.end('car processing');
          });
      }
    },
    restore(id) {
      if (confirm(this.$t('alerts.restore_entity', { entity: this.$t('entities.car') }))) {
        this.$wait.start('car processing');
        new CarModel({
          id: `${id}/restore`,
        })
          .save()
          .then(() => {
            this.$refs.asyncTable.refresh();
            this.$wait.end('car processing');
          })
          .catch(() => {
            this.$wait.end('car processing');
          });
      }
    },
    updateFilter: debounce(function (value) {
      this.filters.license_plate = value;
    }, 500),
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent
          :title="$t('titles.cars')"
          :button-text="$t('titles.create_car')"
          @button-pressed="$router.push({ name: 'cars.create' })"
        ></HeaderComponent>
      </b-col>
    </b-row>
    <b-row class="mb-2">
      <b-col sm="12" lg="4" md="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span
            >&nbsp;
            <small class="pt-01">
              {{ $t('search.message', { entity: $t('search.entities.license_plate') }) }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.license_plate" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter"
            >&nbsp;</b-form-input
          >
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white border-gray" :disabled="!filters.license_plate" @click="filters.license_plate = ''">{{
              $t('actions.clear')
            }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
    </b-row>
    <b-row class="mb-4 d-flex justify-content-end">
      <b-col>
        <b-btn
          class="fa-pull-right cursor-pointer border-gray"
          :variant="showMoreOptions ? 'secondary' : 'outline-secondary'"
          size="md"
          @click.stop="showMoreOptions = !showMoreOptions"
        >
          {{ $t('titles.sva') }}
        </b-btn>
      </b-col>
    </b-row>
    <b-collapse v-model="showMoreOptions">
      <b-card :header="$t('titles.sva_export')" class="mb-4 position-relative">
        <BlockingContainer v-if="$wait.is('downloading-pdf')" :text="$t('messages.export_in_progress')"></BlockingContainer>
        <b-row class="mb-2 flex-column flex-sm-row">
          <b-col sm="12" md="6" lg="5" class="mb-3 mb-lg-0">
            <HeaderComponent>
              <template v-slot:header>
                <span>{{ $t('placeholders.date_interval_start') }}</span>
              </template>
            </HeaderComponent>
            <b-input-group>
              <flat-pickr v-model="svaExport.fromDate" :config="svaExportFromDateConfig" class="form-control-sm" placeholder="Interval Start"
                >&nbsp;</flat-pickr
              >
              <b-input-group-append>
                <b-btn
                  variant="secondary"
                  size="sm"
                  class="text-white border-gray"
                  :disabled="svaExport.fromDate == '' || svaExport.fromDate == null"
                  @click.stop="svaExport.fromDate = ''"
                  >{{ $t('actions.clear') }}</b-btn
                >
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col sm="12" md="6" lg="5" class="mb-3 mb-lg-0">
            <HeaderComponent>
              <template v-slot:header>
                <span>{{ $t('placeholders.date_interval_end') }}</span>
              </template>
            </HeaderComponent>
            <b-input-group>
              <flat-pickr v-model="svaExport.toDate" :config="svaExportToDateConfig" class="form-control-sm" placeholder="Interval End">&nbsp;</flat-pickr>
              <b-input-group-append>
                <b-btn
                  variant="secondary"
                  size="sm"
                  class="text-white border-gray"
                  :disabled="svaExport.toDate == '' || svaExport.toDate == null"
                  @click.stop="svaExport.toDate = ''"
                  >{{ $t('actions.clear') }}</b-btn
                >
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col sm="12" md="12" lg="2">
            <b-button class="w-100 mb-2 mb-sm-0 bg-gray" size="sm" variant="dark" :disabled="$wait.is('downloading-pdf')" @click="exportSVAPdf">{{
              $t('actions.export')
            }}</b-button>
            <b-form-checkbox v-model="includingZub" class="mt-2">{{ $t('fields.including_zub') }}</b-form-checkbox>
          </b-col>
        </b-row>
      </b-card>
    </b-collapse>
    <div id="cars-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('car processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(car_type)="row">
          {{ row.item.car_type ? row.item.car_type.name : '' }}
        </template>
        <template v-slot:cell(car_owner)="row">
          {{ row.item.car_owner ? row.item.car_owner.name : '' }}
        </template>
        <template v-slot:cell(verified)="row">
          <b-form-checkbox v-model="row.item.verified" :value="1" :unchecked-value="0" @change="update(row.item)"> </b-form-checkbox>
        </template>
        <template v-slot:cell(sva_print)="row">
          <b-form-checkbox v-model="row.item.sva_print" :value="1" :unchecked-value="0" @change="update(row.item)"> </b-form-checkbox>
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <FileDownloadLink v-if="row.item.zub" :file="row.item.zub" :removable="false" btn-class="mr-2 btn btn-sm btn-outline-secondary" />
            <b-btn class="mr-2" size="sm" variant="outline-primary" :to="{ name: 'cars.edit', params: { id: row.item.id } }">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn v-if="row.item.deleted_at === null" size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
            <b-btn v-else v-can:superAdministrate size="sm" variant="outline-success" @click.stop="restore(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'trash-restore']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>
    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
  </Layout>
</template>
